import { inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ITopToolbarConfig, emptyTopToolbarConfig } from '../app-navigation/domain/top-toolbar.model';
import { TOOLBAR_STRATEGY_FACTORY_TOKEN } from '../app-navigation/infrastructure/providers/top-toolbar-strategy.token';
import { TopToolbarStrategy } from '../app-navigation/ports/toolbar-strategy.port';
import { filter, map, distinctUntilChanged } from 'rxjs';
import { AppSection } from '../app-navigation/domain/app-section.model';

@Injectable({
  providedIn: 'root',
})
export class TopToolbarManagerService {
  private readonly strategyFactory = inject(TOOLBAR_STRATEGY_FACTORY_TOKEN);
  private readonly section = signal<AppSection | ''>('');
  private readonly router = inject(Router);

  // Section navigation strategies cache
  private readonly strategyCache: Map<string, TopToolbarStrategy> = new Map();
  private readonly CACHE_SIZE_LIMIT = 3;
  private readonly configCache = new Map<string, ITopToolbarConfig>();

  // Promise cache
  private activePromise: Promise<ITopToolbarConfig> | null = null;
  private activeSection: string | null = null;

  constructor() {
    this.setupRoutingListener();
  }

  private setupRoutingListener(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let route = this.router.routerState.root;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route.snapshot.data['section'] || '';
        }),
        distinctUntilChanged(),
      )
      .subscribe((section) => {
        this.setSection(section);
      });
  }

  public setSection(section: AppSection): void {
    this.section.set(section);
  }

  // TODO remove console log
  public async getTopToolbarConfig(): Promise<ITopToolbarConfig> {
    // console.log('[TopToolbarManagerService] --getTopToolbarConfig()--');
    const currentSection = this.section();

    if (!currentSection) {
      return Promise.resolve(emptyTopToolbarConfig());
    }

    // Promise cache
    if (this.activePromise && this.activeSection === currentSection) {
      // console.log('[TopToolbarManagerService] ----getTopToolbarConfig() USING ACTIVE PROMISE----');
      return this.activePromise;
    }

    // Config cache
    if (this.configCache.has(currentSection)) {
      // console.log('[TopToolbarManagerService] ----getTopToolbarConfig() CONFIG IN CACHE----');
      return Promise.resolve(this.configCache.get(currentSection) ?? emptyTopToolbarConfig());
    }

    // New promise
    this.activeSection = currentSection;
    this.activePromise = this.loadTopToolbarConfig(currentSection);

    // Clean promise cache
    this.activePromise.finally(() => {
      this.activePromise = null;
      this.activeSection = null;
    });

    return this.activePromise;
  }

  private async loadTopToolbarConfig(section: string): Promise<ITopToolbarConfig> {
    if (!this.strategyCache.has(section)) {
      // console.log('[TopToolbarManagerService] ----getTopToolbarConfig() LOAD STRATEGY----');
      const strategy = await this.strategyFactory.createStrategy(section);

      if (!strategy) {
        return emptyTopToolbarConfig();
      }

      // Keep cache size
      if (this.strategyCache.size >= this.CACHE_SIZE_LIMIT) {
        // FIFO
        const oldestKey = this.strategyCache.keys().next().value;
        if (oldestKey) {
          this.strategyCache.delete(oldestKey);
          this.configCache.delete(oldestKey);
        }
      }

      this.strategyCache.set(section, strategy);
    }

    const config = this.strategyCache.get(section)?.getTopToolbarConfig() ?? emptyTopToolbarConfig();
    this.configCache.set(section, config);

    return config;
  }
}
